import type { DisplayEntryType } from "./travel.js";

// View read-model contracts (shared by the API producer and the web consumers) +
// the GL-free map transforms (deck.gl renders to WebGL, so the data shaping is a
// pure, unit-testable function — never tested through the canvas).

export type Precision = "exact" | "city" | "country";

export interface MapPoint {
  user_id: string;
  name: string;
  handle: string | null;
  lat: number | null;
  lng: number | null;
  city: string | null;
  country: string | null;
  precision: Precision;
  inTrip: boolean;
  /** home origin (already redacted to the same level) for in-trip arcs */
  origin: { lat: number; lng: number } | null;
  stale: boolean;
  isSelf: boolean;
}

export interface CalendarEvent {
  id: string;
  user_id: string;
  name: string;
  title: string;
  start: string;
  end: string | null;
  type: DisplayEntryType;
  // structured place (for the Lanes view); redaction-aware (city null at country level)
  city: string | null;
  country: string | null;
  country_code: string | null;
}

export interface WorkingHoursEntry {
  user_id: string;
  name: string;
  timezone: string | null;
  working_start: string | null;
  working_end: string | null;
}

export interface ScatterDatum extends MapPoint {
  position: [number, number]; // [lng, lat] for deck.gl
}

export interface ArcDatum {
  user_id: string;
  name: string;
  source: [number, number];
  target: [number, number];
}

/** Map points with coordinates → ScatterplotLayer data. */
export function toScatterData(points: MapPoint[]): ScatterDatum[] {
  return points
    .filter((p): p is MapPoint & { lat: number; lng: number } => p.lat != null && p.lng != null)
    .map((p) => ({ ...p, position: [p.lng, p.lat] }));
}

/** In-trip points with a redacted home origin → ArcLayer data (home → destination). */
export function toArcData(points: MapPoint[]): ArcDatum[] {
  const arcs: ArcDatum[] = [];
  for (const p of points) {
    if (p.inTrip && p.origin && p.lat != null && p.lng != null) {
      arcs.push({
        user_id: p.user_id,
        name: p.name,
        source: [p.origin.lng, p.origin.lat],
        target: [p.lng, p.lat],
      });
    }
  }
  return arcs;
}
